// Trip Roll — off-challenge photo uploads + chronological gallery.
// player_id is always derived server-side from the session cookie.

import { uploadToCloudinary } from './api.js';

const TRIP_DEFAULT = 'juneau-2026';

async function postJson(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  let data;
  try {
    data = await res.json();
  } catch {
    data = {};
  }
  if (!res.ok) {
    const err = new Error(data.message || data.error || `${url} → ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
}

async function getJson(url) {
  const res = await fetch(url, { credentials: 'include' });
  const ct = res.headers.get('content-type') || '';
  // Plain `vite` dev (no `netlify dev`) returns the SPA index.html for
  // unknown routes — JSON.parse would explode. Match the pattern used
  // in excursionsApi / mealsApi: throw with status='api_unavailable'
  // so the UI can degrade silently instead of surfacing a fetch error.
  if (!ct.includes('json')) {
    const err = new Error('api_unavailable');
    err.status = 'api_unavailable';
    throw err;
  }
  if (!res.ok) {
    const err = new Error(`${url} → ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export function newRollId() {
  return `roll_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

async function signTripRollUpload({ tripId, rollId, resourceType = 'image' }) {
  return postJson('/api/trip-roll/sign-upload', {
    trip_id: tripId,
    roll_id: rollId,
    resource_type: resourceType,
  });
}

async function commitTripRoll({ tripId, rollId, photoUrl, resourceType, durationSeconds, caption, location, takenAt, exif }) {
  return postJson('/api/trip-roll', {
    trip_id: tripId,
    roll_id: rollId,
    photo_url: photoUrl,
    resource_type: resourceType,
    duration_seconds: durationSeconds,
    caption,
    claimed_location: location,
    taken_at: takenAt,
    exif: exif || null,
  });
}

// Single high-level helper: file → signed upload → Cloudinary →
// commit Trip Roll record. Returns the signed-URL record the gallery
// uses immediately.
//
// resourceType defaults to 'image' so existing callers keep working
// without changes. Pass 'video' for video uploads; caller is
// responsible for duration + size client-side checks (TripRoll.jsx
// handles both).
export async function uploadTripRollPhoto({
  tripId = TRIP_DEFAULT,
  file,
  caption,
  location,
  onProgress,
  resourceType = 'image',
  durationSeconds = null,
}) {
  const rollId = newRollId();
  const signed = await signTripRollUpload({ tripId, rollId, resourceType });
  const uploaded = await uploadToCloudinary({ file, signed, onProgress });
  return commitTripRoll({
    tripId,
    rollId,
    photoUrl: uploaded.secure_url,
    resourceType,
    durationSeconds,
    caption: caption?.trim() || undefined,
    location,
    takenAt: new Date().toISOString(),
    exif: resourceType === 'image' ? uploaded.image_metadata || null : null,
  });
}

export async function fetchTripRoll({ tripId = TRIP_DEFAULT, limit = 60 } = {}) {
  return getJson(`/api/trip-roll?trip_id=${tripId}&limit=${limit}`);
}
